/**
 * contract.ts — the PlantLifecycle API contract, as TypeScript.
 *
 * `docs/architecture/browser-repository-boundary.md` is unambiguous about where
 * these types may come from:
 *
 *   > The browser repository must not import Engine source, Engine internal
 *   > modules, or Engine build output. It consumes a published, versioned API
 *   > contract.
 *
 * So this file is the contract — `contracts/openapi.yaml` in the Engine
 * repository — expressed in the language this repository is written in. It is
 * the ONLY description of the server's shapes in this codebase; nothing else
 * here declares what a diagram or a change set looks like.
 *
 * Keeping it true is not left to discipline: `npm run check:contract` verifies
 * the repository-owned OpenAPI artifact against its source provenance and
 * fails if this file's version differs or falls outside the supported range.
 * The server also states its version on every session response.
 *
 * Nothing in this file is canonical engineering data. Every shape is a
 * projection the Engine computed; the browser renders and never re-derives.
 */

// ---------------------------------------------------------------------------
// Contract version
// ---------------------------------------------------------------------------

/** The contract version this file was written against. */
export const CONTRACT_VERSION = "1.0.0";

/**
 * The server versions this client accepts, as declared by the boundary
 * document's "compatibility declaration".
 */
export const SUPPORTED_API_RANGE = ">=1.0 <2.0";

// ---------------------------------------------------------------------------
// Geometry — millimetres, always. Never pixels.
// ---------------------------------------------------------------------------

/** A point in world millimetres on the sheet. */
export interface PointMm { x: number; y: number }

/** An axis-aligned rectangle in world millimetres. */
export interface RectMm { x: number; y: number; w: number; h: number }

// ---------------------------------------------------------------------------
// Errors — §10.5. Every failure has one of these codes; none has free text
// the client is expected to parse.
// ---------------------------------------------------------------------------

export const APPLICATION_ERROR_CODES = [
  "AUTH_REQUIRED", "FORBIDDEN", "PROJECT_NOT_FOUND", "RESOURCE_NOT_FOUND",
  "ENGINE_PROJECT_INCOMPATIBLE", "STALE_CANONICAL_REVISION", "RESOURCE_CONFLICT",
  "PLAN_INVALID", "VALIDATION_BLOCKING", "LIFECYCLE_GATE_FAILED", "GIT_PUBLISH_FAILED",
  "JOB_FAILED", "BAD_REQUEST", "CHANGE_SET_STATE_INVALID", "RATE_LIMITED", "INTERNAL_ERROR",
] as const;

export type ApplicationErrorCode = (typeof APPLICATION_ERROR_CODES)[number];

export interface ApplicationErrorBody {
  code: ApplicationErrorCode;
  message: string;
  details?: Record<string, unknown>;
  /** Correlates this failure with the server log line. Show it in the UI. */
  requestId?: string;
}

// ---------------------------------------------------------------------------
// Identity and session
// ---------------------------------------------------------------------------

export const APPLICATION_ROLES = ["Viewer", "Engineer", "DisciplineLead", "ProjectLead", "Administrator"] as const;
export type ApplicationRole = (typeof APPLICATION_ROLES)[number];

export type ActorType = "human" | "agent" | "service" | "system";

export interface Identity {
  subject: string;
  actorType: ActorType;
  displayName?: string;
  roles: ApplicationRole[];
}

export interface ProjectMembership {
  projectId: string;
  role: ApplicationRole;
}

export interface SessionView {
  authenticated: boolean;
  identity?: Identity;
  projects: ProjectMembership[];
  api: { version: string; engineVersion: string };
}

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

export interface ProjectSummary {
  projectId: string;
  title: string;
  canonicalRevision: string;
  lifecycle?: Record<string, string>;
}

export interface ProjectStatus extends ProjectSummary {
  engine: { version: string; projectLockVersion?: string; compatible: boolean; incompatibility?: string };
  diagrams: DiagramSummary[];
  changeSets: ChangeSetSummary[];
  validation: ValidationSummary;
  symbolDependencies: Array<{ alias: string; symbolId: string; variantId: string; version: string }>;
}

// ---------------------------------------------------------------------------
// Diagrams and the render projection
// ---------------------------------------------------------------------------

export interface DiagramSummary {
  diagramId: string;
  title: string;
  diagramType: string;
  retired: boolean;
  symbolCount: number;
  traceCount: number;
  /** Monotonic per-resource revision. Send it back on every edit (§9.1). */
  revision: number;
}

export interface DiagramView extends DiagramSummary {
  projection: DiagramRenderProjection;
  canonicalRevision: string;
}

export interface PrimitiveStyle {
  stroke?: string;
  /** Millimetres. */
  strokeWidth?: number;
  fill?: string;
  opacity?: number;
  dasharray?: string;
}

/**
 * Drawable geometry, in SYMBOL-LOCAL millimetres.
 *
 * The client applies the instance origin and rotation when drawing. That is a
 * presentation transform over geometry the Engine already resolved — it is not
 * the client deciding where anything is.
 */
export type SymbolPrimitive =
  | { kind: "line"; id: string; x1: number; y1: number; x2: number; y2: number; style?: PrimitiveStyle }
  | { kind: "rect"; id: string; x: number; y: number; width: number; height: number; style?: PrimitiveStyle }
  | { kind: "roundRect"; id: string; x: number; y: number; width: number; height: number; rx?: number; ry?: number; style?: PrimitiveStyle }
  | { kind: "circle"; id: string; cx: number; cy: number; r: number; style?: PrimitiveStyle }
  | { kind: "ellipse"; id: string; cx: number; cy: number; rx: number; ry: number; style?: PrimitiveStyle }
  | { kind: "arc"; id: string; x: number; y: number; width: number; height: number; startAngleDeg: number; sweepAngleDeg: number; style?: PrimitiveStyle }
  | { kind: "path"; id: string; d: string; style?: PrimitiveStyle }
  | { kind: "polyline"; id: string; points: Array<[number, number]>; style?: PrimitiveStyle }
  | { kind: "polygon"; id: string; points: Array<[number, number]>; style?: PrimitiveStyle }
  | { kind: "text"; id: string; x: number; y: number; text: string; fontSize?: number; fontFamily?: string; textAnchor?: "start" | "middle" | "end"; fill?: string; style?: PrimitiveStyle }
  | { kind: "group"; id: string; children: SymbolPrimitive[]; rotationDeg?: number; scaleX?: number; scaleY?: number };

export interface ProjectedPort {
  id: string;
  center: PointMm;
  /** Where a trace attaches. */
  boundary: PointMm;
  normal: { x: -1 | 0 | 1; y: -1 | 0 | 1 };
  dir: "N" | "E" | "S" | "W";
  role: string;
  kindFamily: string;
  kindSubfamily?: string;
  /**
   * The authored semantic role — inlet, outlet, tap. This, and never geometry,
   * is what says which way process flow goes.
   */
  flowDirection?: string;
  label?: string;
  description?: string;
  connected: boolean;
  decorationAnchor?: PointMm;
}

export interface ProjectedSymbol {
  id: string;
  defId: string;
  objectId?: string;
  origin: PointMm;
  rotation: 0 | 90 | 180 | 270;
  flipX?: boolean;
  flipY?: boolean;
  bbox: RectMm;
  geometry: SymbolPrimitive[];
  ports: ProjectedPort[];
  labelAnchors: Record<string, PointMm>;
  defName?: string;
}

export interface ProjectedTrace {
  id: string;
  traceDomain: string;
  traceKind: string;
  traceRole?: string;
  source: { symbolId: string; portId: string };
  target: { symbolId: string; portId: string };
  /**
   * The routed path in world mm, in STORAGE order. Storage order is not flow
   * direction and must never be drawn as an arrow.
   */
  path: PointMm[];
  routeStatus?: string;
  /** DIN EN ISO 10628 line width for this domain, in millimetres. */
  lineWidthMm: number;
  lineNumber?: string;
  traceName?: string;
  fluid?: string;
  DN?: number;
  PN?: number;
  cableName?: string;
  coreLabel?: string;
}

export interface ProjectedAnnotation {
  id: string;
  kind: string;
  position: PointMm;
  w?: number;
  h?: number;
  text?: string;
}

export interface DiagramRenderProjection {
  projectionVersion: string;
  diagramId: string;
  diagramType: string;
  title: string;
  retired: boolean;
  sheet: { format: string; widthMm: number; heightMm: number; orientation: string };
  symbols: ProjectedSymbol[];
  traces: ProjectedTrace[];
  annotations: ProjectedAnnotation[];
  /** What the Engine could not resolve. Shown as a defect, never hidden. */
  unresolved: Array<{ kind: "symbol" | "trace"; id: string; reason: string }>;
}

// ---------------------------------------------------------------------------
// Change sets — §8.3
// ---------------------------------------------------------------------------

export const CHANGE_SET_STATUSES = [
  "draft", "validating", "ready_for_review", "changes_requested",
  "approved", "publishing", "published", "abandoned", "conflicted",
] as const;
export type ChangeSetStatus = (typeof CHANGE_SET_STATUSES)[number];

export interface ChangeSetSummary {
  changeSetId: string;
  projectId: string;
  title: string;
  status: ChangeSetStatus;
  createdBy: string;
  createdAt: string;
  baseCanonicalRevision: string;
  resultCanonicalRevision?: string;
}

export interface ChangeSet extends ChangeSetSummary {
  changedResources: string[];
  validationSummary: ValidationSummary;
  reviewState: {
    approvals: Array<{ subject: string; at: string }>;
    changesRequested: Array<{ subject: string; at: string; note: string }>;
  };
  operationCount: number;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export interface ValidationFinding {
  code: string;
  severity: "error" | "warning" | "info";
  message: string;
  ref?: { kind: string; id?: string; diagramId?: string };
}

export interface ValidationSummary {
  blocking: number;
  advisory: number;
  findings: ValidationFinding[];
  canonicalRevision: string;
}

// ---------------------------------------------------------------------------
// Authoring — §12 preview, then apply
// ---------------------------------------------------------------------------

export interface AuthoringOperationInput {
  operationId: string;
  type: string;
  command: Record<string, unknown>;
}

export interface AuthoringRequest {
  operations: AuthoringOperationInput[];
  /** The resource revision the client believed it was editing (§9.1). */
  base?: { diagramId: string; revision: number };
}

/** One decision the Engine made while resolving an intent, in reviewable form. */
export interface AuthoringResolution {
  operationId: string;
  kind: string;
  detail: Record<string, unknown>;
}

export interface AuthoringPreview {
  ok: boolean;
  /** What every intent resolved into. This is what a reviewer reads. */
  normalizedOperations: AuthoringOperationInput[];
  resolutions: AuthoringResolution[];
  errors: string[];
  warnings: string[];
  canonicalDiff: Array<{ resource: string; targetType?: string; before: string | null; after: string | null }>;
}

export interface AuthoringResult extends AuthoringPreview {
  applied: boolean;
  canonicalRevision?: string;
  diagramRevision?: number;
  auditId?: string;
}

// ---------------------------------------------------------------------------
// Symbols
// ---------------------------------------------------------------------------

export interface SymbolCatalogEntry {
  defId: string;
  name: string;
  domain: string;
  family: string;
  portCount: number;
  /** Present when the definition comes from the controlled registry. */
  dependency?: { alias: string; symbolId: string; variantId: string; version: string; versionRange?: string };
}

// ---------------------------------------------------------------------------
// Jobs, events, audit
// ---------------------------------------------------------------------------

export type JobStatus = "queued" | "running" | "succeeded" | "failed";

export interface JobView {
  jobId: string;
  projectId: string;
  kind: string;
  status: JobStatus;
  createdAt: string;
  finishedAt?: string;
  error?: { code: string; message: string };
  result?: unknown;
}

export type ApplicationEventKind =
  | "project.revision.changed"
  | "changeset.status.changed"
  | "validation.completed"
  | "job.status.changed"
  | "presence.changed"
  | "conflict.detected";

export interface ApplicationEvent {
  kind: ApplicationEventKind;
  projectId: string;
  at: string;
  payload: Record<string, unknown>;
}

export interface AuditEvent {
  eventId: string;
  projectId: string;
  actorType: ActorType;
  actorId: string;
  actorDisplayName?: string;
  sourceClient: string;
  requestId: string;
  changeSetId?: string;
  baseCanonicalRevision?: string;
  resourceIds: string[];
  operation: string;
  timestamp: string;
  result: "accepted" | "refused" | "failed";
  resultingRevision?: string;
}
